import { fg } from '@atlaskit/platform-feature-flags';
import { getAssignees } from '@atlassian/search-client/assignees';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { createClient } from '@atlassian/search-client/base-search-client';
import { Scope as JiraScope } from '@atlassian/search-client/jira/types';
import { type User, usersQuery } from '@atlassian/search-client/user/user';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getUrsProductKey } from '../get-urs-product-key';
import { loadRecommendedUsers } from '../utils/recommendedUser';

const MAX_SEARCHED_OPTIONS_RESULTS = 5;

const loadInitialOptions = async (config: CloudConfig) => {
	if (config.product === ProductKeys.JiraDC || config.product === ProductKeys.JsmDC) {
		return loadRecommendedUsers(config);
	}
	return searchJiraCloudAssignee('', config, { initialOptions: true });
};

const searchAssignee = async (query: string, config: CloudConfig) => {
	if (config.product === ProductKeys.JiraDC || config.product === ProductKeys.JsmDC) {
		return searchAssignees3P(query, config);
	}
	return searchJiraCloudAssignee(query, config);
};

const searchAssignees3P = async (query: string, config: CloudConfig) => {
	if (!config.intl) {
		return []; // base on existing interface, can only query with intl
	}

	const response = await getAssignees({
		query,
		cloudId: config.cloudId || '',
		userId: config.userId || '',
		productKey: fg('search_page_filter_product_key')
			? getUrsProductKey(config.primaryProduct)
			: ProductKeys.Confluence,
		maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
		intl: config.intl,
	});

	if (!response) {
		return [];
	}

	return response.map((assignee) => ({
		trackingKey: assignee.id,
		value: assignee.id,
		label: assignee.name,
		avatarUrl: assignee.avatarUrl,
		queryParamValue: assignee.id,
		product: config.product,
	}));
};

const searchJiraCloudAssignee = async (
	query: string,
	config: CloudConfig,
	options?: { initialOptions: boolean },
) => {
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const initialUsersQuery =
		config.userId && options?.initialOptions
			? usersQuery([config.userId], headers)
			: Promise.resolve({ data: { users: [] } });

	const searchClient = createClient({
		cloudId: config.cloudId || '',
	});

	const searchAssigneeQuery = searchClient.search({
		query,
		resultLimit: 5,
		scopes: [JiraScope.People],
		analytics: {
			identifier: 'search-page',
		},
	});

	const [initialUsersResponse, searchAssigneeReponse] = await Promise.all([
		initialUsersQuery,
		searchAssigneeQuery,
	]);
	const searchAssigneeResults = searchAssigneeReponse.response.rawData.scopes[0]?.results ?? [];

	const allAssignees = [
		// The current user is always first
		...(initialUsersResponse.data?.users.map((user) => ({
			...user,
			id: user.accountId,
		})) ?? []),
		...searchAssigneeResults.map((user) => ({
			picture: user.avatarUrl,
			name: user.name,
			id: user.id,
			accountId: user.id,
		})),
	] satisfies User[];

	const userIds = new Set();

	const assigneeOptions = allAssignees
		.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
		.map((user) => ({
			trackingKey: user.id,
			value: user.id,
			label: user.name,
			avatarUrl: user.picture,
			queryParamValue: user.accountId,
			product: config.product,
		}));

	return assigneeOptions;
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('assignee');

export const filter: Readonly<{
	id: 'assignee';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'assignee'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assignee'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assignee'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'assignee',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'jAssignee',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersResponse = await usersQuery(queryParams, headers);

			const allAssignees = initialUsersResponse.data?.users ?? ([] satisfies User[]);

			const userIds = new Set();

			const assigneeOptions = allAssignees
				.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
				.map((user) => ({
					trackingKey: user.accountId,
					value: user.accountId,
					label: user.name,
					avatarUrl: user.picture,
					queryParamValue: user.accountId,
					product: config.product,
				}));
			return assigneeOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadInitialOptions(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchAssignee(query, config);
		},
	},
	isSuggestedFilter: (primaryProduct) =>
		primaryProduct === ProductKeys.Jira ||
		primaryProduct === ProductKeys.JiraDC ||
		primaryProduct === ProductKeys.JsmDC ||
		primaryProduct === ProductKeys.JiraServiceManagement ||
		primaryProduct === ProductKeys.JiraProductDiscovery,
});
